import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { SELECTABLE_ROLES } from "@/constants/permissions";
import type { Permission, Role } from "@/lib/permissions";
import { userMock } from "@/mocks/user";
import type { CurrentUser } from "@/types";

const INITIAL_USER: CurrentUser = {
  ...userMock,
  role: undefined as unknown as Role,
};

export type AuthTransition = "idle" | "login" | "logout" | "switch-role";

interface UserState {
  user: CurrentUser;
  authenticatedAccountId: string | null;
  availableRoles: Role[];
  preferredRole: Role | null;
  preferredRoleLoaded: boolean;
  permissions: Permission[];
  permissionsInitialized: boolean;
  hasHydrated: boolean;
  authTransition: AuthTransition;
  setUser: (user: Partial<CurrentUser>) => void;
  setAuthenticatedAccountId: (accountId: string | null) => void;
  setAuthTransition: (transition: AuthTransition) => void;
  clearSession: () => void;
  clearRole: () => void;
  setRole: (role: Role) => void;
  setAvailableRoles: (roles: Role[]) => void;
  setPreferredRole: (role: Role | null) => void;
  clearPreferredRole: () => void;
  initializeRoles: (roles: Role[]) => void;
  initializePermissions: (permissions: Permission[]) => void;
  clearPermissions: () => void;
  setHasHydrated: (value: boolean) => void;
}

function resolveSessionRole(
  state: Pick<UserState, "user" | "availableRoles" | "preferredRole" | "preferredRoleLoaded">
): Pick<UserState, "user"> | Record<string, never> {
  const roles = state.availableRoles;
  if (roles.length === 0 || !state.preferredRoleLoaded) return {};
  if (state.user.role && roles.includes(state.user.role)) return {};
  const nextRole =
    state.preferredRole && roles.includes(state.preferredRole) ? state.preferredRole : roles[0];
  return { user: { ...state.user, role: nextRole } };
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      user: INITIAL_USER,
      authenticatedAccountId: null,
      availableRoles: [],
      preferredRole: null,
      preferredRoleLoaded: false,
      permissions: [],
      permissionsInitialized: false,
      hasHydrated: false,
      authTransition: "idle",
      setHasHydrated: (value) => set({ hasHydrated: value }),
      setUser: (user) => set((state) => ({ user: { ...state.user, ...user } })),
      setAuthenticatedAccountId: (accountId) => set({ authenticatedAccountId: accountId }),
      setAuthTransition: (transition) => set({ authTransition: transition }),
      clearSession: () =>
        set((state) => ({
          user: { ...INITIAL_USER },
          authenticatedAccountId: null,
          availableRoles: [],
          preferredRole: null,
          preferredRoleLoaded: false,
          permissions: [],
          permissionsInitialized: false,
          hasHydrated: state.hasHydrated,
          authTransition: state.authTransition,
        })),
      clearRole: () =>
        set((state) => ({ user: { ...state.user, role: undefined as unknown as Role } })),
      setRole: (role) => set((state) => ({ user: { ...state.user, role } })),
      setAvailableRoles: (roles) => set({ availableRoles: roles }),
      clearPreferredRole: () => set({ preferredRole: null, preferredRoleLoaded: false }),
      setPreferredRole: (role) =>
        set((state) => ({
          preferredRole: role,
          preferredRoleLoaded: true,
          ...resolveSessionRole({
            ...state,
            preferredRole: role,
            preferredRoleLoaded: true,
          }),
        })),
      initializePermissions: (permissions) => set({ permissions, permissionsInitialized: true }),
      clearPermissions: () => set({ permissions: [], permissionsInitialized: false }),
      initializeRoles: (roles) =>
        set((state) => ({
          availableRoles: roles,
          ...resolveSessionRole({ ...state, availableRoles: roles }),
        })),
    }),
    {
      name: "empiriq-user-role",
      storage: createJSONStorage(() => sessionStorage),
      skipHydration: true,
      partialize: (state) => ({ user: { role: state.user.role } }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as { user?: { role?: Role } };
        const persistedRole = persisted?.user?.role;
        const isValidRole = persistedRole && (SELECTABLE_ROLES as string[]).includes(persistedRole);
        return {
          ...currentState,
          user: {
            ...currentState.user,
            ...(isValidRole ? { role: persistedRole } : { role: undefined as unknown as Role }),
          },
        };
      },
    }
  )
);
